import { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { Button, StatusLine, VisuallyHidden } from 'admination-react'
import type { SaveState } from '@/core/lib/autosave'

export type AutosaveEntry = { snapshot: () => SaveState; subscribe: (fn: () => void) => () => void; flush: () => Promise<unknown> | undefined }
type Boundary = { identity: string; label?: string; entries: ReadonlyMap<string, AutosaveEntry>; register: (key: string, entry: AutosaveEntry) => void; subscribe: (fn: () => void) => () => void }
export const AutosaveContext = createContext<Boundary | null>(null)
const boundaries = new Map<string, Boundary>()

export function AutosaveBoundary({ identity, label, children }: { identity: string; label?: string; children: ReactNode }) {
  let boundary = boundaries.get(identity)
  if (!boundary) {
    const listeners = new Set<() => void>()
    const entries = new Map<string, AutosaveEntry>()
    const subscriptions = new Map<string, () => void>()
    const changed = () => { for (const fn of listeners) fn() }
    boundary = {
      identity, entries,
      register: (key, entry) => {
        if (entries.has(key)) return
        entries.set(key, entry)
        if (listeners.size) subscriptions.set(key, entry.subscribe(changed))
        changed()
      },
      subscribe: (fn) => {
        listeners.add(fn)
        if (listeners.size === 1) {
          for (const [key, entry] of entries) subscriptions.set(key, entry.subscribe(changed))
        }
        return () => {
          listeners.delete(fn)
          if (!listeners.size) {
            for (const unsubscribe of subscriptions.values()) unsubscribe()
            subscriptions.clear()
          }
        }
      },
    }
    boundaries.set(identity, boundary)
  }
  boundary.label = label
  return <AutosaveContext.Provider value={boundary}>{children}</AutosaveContext.Provider>
}

export function AutosaveStatus({
  state: suppliedState,
  identity,
  onRetry,
  labels = { saved: 'Gespeichert', error: 'Nicht gespeichert', retry: 'Erneut versuchen' },
}: {
  state?: SaveState
  identity?: string | number
  onRetry?: () => unknown
  labels?: { saved: string; error: string; retry: string }
} = {}) {
  const boundary = useContext(AutosaveContext)
  const snapshot = () => {
    const states = boundary ? [...boundary.entries.values()].map((entry) => entry.snapshot()) : []
    return !states.length ? null : states.includes('error') ? 'error' : states.includes('saving') ? 'saving' : states.includes('saved') ? 'saved' : 'idle'
  }
  const boundaryState = useSyncExternalStore(boundary?.subscribe ?? (() => () => {}), snapshot, () => null)
  const state = suppliedState ?? boundaryState
  const scope = identity ?? boundary?.identity
  const previous = useRef({ identity: scope, state })
  const [announcement, setAnnouncement] = useState('')
  useEffect(() => {
    setAnnouncement(state === 'saved' && previous.current.state === 'saving' && previous.current.identity === scope ? labels.saved : '')
    previous.current = { identity: scope, state }
  }, [state, scope, labels.saved])
  if (state === null) return null
  if (state !== 'error') return <VisuallyHidden role="status" aria-live="polite">{announcement}</VisuallyHidden>
  return <div className="autosave-status">
    <StatusLine role="status" aria-live="polite" tone="error">{labels.error}</StatusLine>
    <Button onClick={() => {
      if (onRetry) { void onRetry(); return }
      for (const entry of boundary?.entries.values() ?? []) void entry.flush()?.catch(() => {})
    }}>{labels.retry}</Button>
  </div>
}
